import React, { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { initialize } from 'redux-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Field, Form, getFormValues, reduxForm } from 'redux-form'

import { Button, CircularProgress, Grid, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import ErrorMessage from '../../components/ErrorMessage'
import withInit from '../../components/containers/withInit'
import { useFetch } from '../../utils/hooks'
import registerInterestFields from '../../config/register-interest-fields'
import validationService from '../../services/validation-service'
import { registerInterestXero } from '../../utils/helpers'
import { registerInterestXeroValidationSchema } from '../../utils/validationSchema'
import styles from './RegisterInterestXeroFormStyles'

const useStyles = makeStyles(styles)

const API_URL = window.ENVIRONMENT && window.ENVIRONMENT.apiRootUrl
    ? `${window.ENVIRONMENT.apiRootUrl}/api`
    : 'https://icompleatcentralportal-qa.azurewebsites.net/api'

const RegisterInterestXeroForm = props => {
    const classes = useStyles()

    const [searchParams] = useSearchParams();
    const code = searchParams.get('code');

    const {
        error,
        handleSubmit,
        asyncValidating,
        pristine,
        valid,
        submitting,
        setIsSubmitting,
        isSubmitting,
    } = props

    const interestRegistered = useSelector(
        (state) => state.form.registerInterestXeroForm?.submitSucceeded
    );

    const isSubmitError = useSelector(
        (state) => state.form.registerInterestXeroForm?.submitFailed
    );

    const [, , userDetails] = useFetch(`${API_URL}/register/interest/xero/user?code=${code ?? ''}`)


    const submitDisabled = !!(pristine || !valid || asyncValidating)

    useEffect(() => {
        document.title = 'Register Interest'
    }, [])

    useEffect(() => {
        if (submitting) {
            setIsSubmitting(true)
        }
    }, [submitting, setIsSubmitting])

    useEffect(() => {
        if (error || isSubmitError) setIsSubmitting(false)
    }, [error, isSubmitError, setIsSubmitting])

    useEffect(() => {
        let url = new URL(document.baseURI);
        window.history.replaceState({}, document.title, `${url.origin}${url.pathname}`);
    });

    const dispatch = useDispatch();
    useEffect(() => {
        if (userDetails && Object.keys(userDetails).length > 0) {
            dispatch(initialize('registerInterestXeroForm', userDetails));
        }
    }, [userDetails, dispatch]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item md={12} sm={12} xs={12}>
                    <Typography component="h3" variant="h5" color="primary" className={classes.title}>
                        Purchasing & AP Automation with Xero
                    </Typography>
                </Grid>
                <Grid item md={12} sm={12} xs={12}>
                    <Typography component="p" variant="p">
                        Ready to stop chasing invoices and start controlling spend before it happens? Compleat's purchasing and accounts payable automation works seamlessly with Xero to give you full visibility, control, and compliance without the admin grind.
                    </Typography>
                </Grid>
                <Grid item md={12} sm={12} xs={12}>
                    <Typography component="p" variant="p">
                        Complete the form to register your interest today and discover how much time and money you could save with your purchasing and AP automation journey with Compleat and Xero.
                    </Typography>
                </Grid>
            </Grid>

            <div className={classes.separator} />

            {interestRegistered && (
                <Grid container spacing={2}>
                    <Grid item container md={12} sm={12} xs={12}>
                        <Paper elevation={0} className={classes["interestRegistered"]}>
                            Your interest has been registered and the team will be in touch soon.
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {!interestRegistered && (
                <Form className={classes.form} onSubmit={handleSubmit} defaultValue>
                    <Grid container spacing={2}>
                        <Grid item md={12} sm={12} xs={12}>
                            <Typography component="h2" variant="h6" className={classes.personalDetails}>
                                Personal Details
                            </Typography>
                        </Grid>
                        <Grid item md={6} sm={6} xs={12}>
                            <Field {...registerInterestFields.firstName} disabled={isSubmitting} />
                        </Grid>
                        <Grid item md={6} sm={6} xs={12}>
                            <Field {...registerInterestFields.lastName} disabled={isSubmitting} />
                        </Grid>
                        <Grid item md={6} sm={6} xs={12}>
                            <Field {...registerInterestFields.email} disabled={isSubmitting} />
                        </Grid>
                        <Grid item md={6} sm={6} xs={12}>
                            <Field {...registerInterestFields.phoneNumber} disabled={isSubmitting} />
                        </Grid>
                        <Grid item md={12} sm={12} xs={12}>
                            <Field {...registerInterestFields.companyName} disabled={isSubmitting} />
                        </Grid>

                        <Grid item md={12} xs={12}>
                            {isSubmitError && (
                                <ErrorMessage error="Oops! Something went wrong while registering your interest. Please try again  in a few minutes." />
                            )}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                color="primary"
                                className={classes.submit}
                                disabled={submitDisabled || isSubmitting}
                            >
                                {isSubmitting && <CircularProgress size={16} className={classes.spinner} />}
                                Register Interest
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </>
    )
}

const ReduxRegisterInterestXeroForm = reduxForm({
    form: 'registerInterestXeroForm',
    validate: validationService.syncValidation(registerInterestXeroValidationSchema),
    onSubmit: registerInterestXero,
    enableReinitialize: false,
})(RegisterInterestXeroForm)

const mapStateToProps = state => {
    const {
        form: { registerInterestXeroForm },
    } = state

    return {
        formValues: getFormValues('registerInterestXeroForm')(state),
        registerInterestXeroForm,
    }
}

const connectedRegisterInterestXeroForm = connect(mapStateToProps)(ReduxRegisterInterestXeroForm)

export default withInit(connectedRegisterInterestXeroForm)
